/**
 * Appends a post-signup destination to a quiz URL as the `nav` query param.
 * The quiz reads `nav` and redirects there once the quiz (and its inline signup)
 * completes — see pages/Quiz.tsx + pages/OnboardingQuiz.tsx. `postSignupUrl` is
 * an app path like "/events/some-event"; the leading slash is dropped because the
 * quiz navigates to `/${nav}`.
 */
export function withPostSignupNav(quizUrl: string, postSignupUrl?: string): string {
  if (!postSignupUrl) return quizUrl;
  const nav = postSignupUrl.replace(/^\//, "");
  const sep = quizUrl.includes("?") ? "&" : "?";
  return `${quizUrl}${sep}nav=${encodeURIComponent(nav)}`;
}

/**
 * Where to send the user after their quiz answers are saved (Quiz.tsx).
 *
 * The default is the dashboard. Callers pass `nav` through as-is rather than
 * defaulting it to "booking": they used to, which made the dashboard branch
 * unreachable and sent every signup into the booking flow whether the guided
 * experiment was on or not.
 *
 * Guided post-signup booking experiment: when the flag is on AND the user was
 * acquired via a typed ad landing (stored ad event type present), a generic
 * destination (absent, or an explicit "booking"/"dashboard") is replaced with
 * the guided /booking/start screen. An explicit `nav` pointing anywhere else
 * (e.g. the event page the user was viewing when the onboarding gate opened)
 * always wins — the guided screen must never hijack a deliberate destination.
 */
export function resolvePostQuizDestination(
  nav: string | null,
  guidedBookingEnabled: boolean,
  adEventType: string | null,
): string {
  const isGenericDestination = !nav || nav === "booking" || nav === "dashboard";
  if (guidedBookingEnabled && adEventType && isGenericDestination) {
    return "/booking/start";
  }
  if (nav) return `/${nav}`;
  return "/dashboard";
}
